import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import { DOCUMENT, NgClass } from "@angular/common";
import { HeaderComponent } from "@ponder-features/header/header.component";
import { ContentComponent } from "@ponder-features/content/content.component";
import { FooterComponent } from "@ponder-features/footer/footer.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { ReactiveFormsModule } from "@angular/forms";
import { AuthService } from "@ponder-core/auth/auth.service";
import { SidebarComponent } from "@ponder-features/sidebar/sidebar.component";
import { getScreenBreakpoint } from "@ponder-shared/utils/screenBreakpoints";
import { ItemSizeService } from "@ponder-shared/previewItem/item-size.service";
import { Subscription } from "rxjs";
import { DataBroker } from "@ponder-core/data/dataBroker";
import { ErrorPopUpContainerComponent } from "@ponder-shared/components/errors/error-popup-container.component";

@Component({
    selector: "app-root",
    imports: [
        HeaderComponent,
        ContentComponent,
        FooterComponent,
        ReactiveFormsModule,
        ErrorPopUpContainerComponent,
        NgClass
    ],
    templateUrl: "./app.component.html"
})
export class AppComponent implements OnInit { 
    title = "Ponder";

    @ViewChild("headerContainer", {read: ElementRef, static: true}) headerContainerRef!: ElementRef<HTMLElement>;
    @ViewChild("contentContainer", {read: ElementRef, static: true}) contentContainerRef!: ElementRef<HTMLElement>;
    @ViewChild("contentElement", {read: ElementRef, static: true}) contentElementRef!: ElementRef<HTMLElement>;

    public standardLayout: boolean = true;
    private currYOffset: number = 0;
    private subscriptions: Subscription[] = [];

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private events: EventBroker,
        private data: DataBroker,
        private viewContainer: ViewContainerRef,
        private authService: AuthService,
        private itemSize: ItemSizeService
    ) {
        if (window.location.pathname.startsWith('/acp')) {
            this.standardLayout = false;
        }

        this.createCssLink("https://fonts.googleapis.com/css?family=Lato:400,700");
        this.registerGlobalEvents();
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    public ngOnInit(): void {
        this.authService.registerAuthChangeCallback();
    }

    public ngAfterViewInit(): void {
        this.adjustContentPadding();
    }

    private createCssLink(url: string): void {
        let link: HTMLLinkElement = this.document.createElement("link");

        link.setAttribute("rel", "stylesheet");
        link.setAttribute("type", "text/css");
        link.setAttribute("href", url);

        this.document.head.appendChild(link);
    }

    private registerGlobalEvents(): void {
        this.subscriptions = [
            this.events.auth.logoutSuccessfulEvent.subscribe(() => window.location.reload()),
            this.events.auth.loginSuccessfulEvent.subscribe(() => window.location.reload()),
            this.events.ui.headerSizeChangeEvent.subscribe(() => this.adjustContentPadding()),
            this.events.ui.displaySidebarEvent.subscribe(() => this.viewContainer.createComponent(SidebarComponent)),
            this.events.ui.removeContentPaddingEvent.subscribe(() => this.contentElementRef?.nativeElement.classList.remove('p-5')),
            this.events.ui.restoreContentPaddingEvent.subscribe(() => this.contentElementRef?.nativeElement.classList.add('p-5')),
            this.data.ui.requestHeaderHeight.subscribe((cb) => { cb(this.headerContainerRef.nativeElement.offsetHeight); })
        ];
    }
    
    @HostListener('window:resize', ['$event'])
    private onResize(): void {
        this.itemSize.recalculate();
        
        this.events.ui.windowResizeEvent.emit({ 
            width: window.screen.width, 
            height: window.screen.height,
            breakpoint: getScreenBreakpoint()
        });
    }
    
    @HostListener("window:scroll", ["$event"])
    private onWindowScrollEvent(): void {
        this.events.ui.windowScrollEvent.emit({
            prevYOffset: this.currYOffset,
            currYOffset: window.scrollY,
            scrolledDown: (window.scrollY > this.currYOffset),
            scrolledUp: (window.scrollY < this.currYOffset)
        });
        
        this.currYOffset = window.scrollY;
    }
    
    @HostListener("window:scrollend", ["$event"])
    private onWindowScrollEndEvent(): void {
        this.events.ui.windowScrollEvent.emit({
            prevYOffset: this.currYOffset,
            currYOffset: window.scrollY,
            scrolledDown: (window.scrollY > this.currYOffset),
            scrolledUp: (window.scrollY < this.currYOffset)
        });
        
        this.currYOffset = window.scrollY;
    }

    private adjustContentPadding(): void {
        let headerHeight = `${this.headerContainerRef.nativeElement.offsetHeight}px;`;
        this.contentContainerRef.nativeElement.style.setProperty("padding-top", headerHeight);
    }
}
